import { ClientProjectEntity, ClientSiteEntity, ClientRemoteServiceEntity } from '@edgebox/api-rest-client';
import { CustomerRole, InternalRole, UserType } from '@edgebox/data-definitions';
import { EditButton, LeftRightContainer, Right, Button, Col, Image, Modal, Row, Nav, Form } from '@edgebox/react-components';
import React from 'react';
import { ApiComponent, ContractLink, CustomerLink, IApiComponentState, ProjectForm, SyncCoreLink } from '../../../common/index';
import {
  ensureProjectOfType,
  ILocationProp,
  INavigateProp,
  IProjectParamProp,
  withLocationAndNavigate,
  withProjectParam,
} from '../../RouterHelper';
import { ContentBox } from '../../Shared/ContentBox';
import ProjectDetailImage from '../../../images/undraw_Mind_map_re_nlb6.svg';
import { IconSummaryItem } from '../../Shared/IconSummaryItem';
import { faFingerprint } from '@fortawesome/pro-light-svg-icons/faFingerprint';
import { faIdCard } from '@fortawesome/pro-light-svg-icons/faIdCard';
import { faBuilding } from '@fortawesome/pro-light-svg-icons/faBuilding';
import { faDollarSign } from '@fortawesome/pro-light-svg-icons/faDollarSign';
import { Product, ProjectStatus, SiteApplicationType, SyncCoreDataDefinitionsEnumTranslator } from '@edgebox/sync-core-data-definitions';
import { HasNone } from '../../../common/helpers/HasSwitch';
import { faServer } from '@fortawesome/pro-light-svg-icons/faServer';
import { propertyChangeReload } from '../../../common/helpers/propertyChangeReload';
import { IAppContextProp, withAppContext } from '../../../common/contexts/AppContext';
import { ProjectIcons } from '../../Icons';
import { UserRoleGate } from '../../../common/components/UserRoleGate';
import { Embed } from '../../Shared/Embed';
import { SyncCoreDataDefinitionsEnumValue } from '../../../common/components/Localization/SyncCoreDataDefinitionsEnumValue';
import EnvironmentCollection from '../ContentCloud/Content/EnvironmentCollection';
import LocaleCollection from '../ContentCloud/Content/LocaleCollection';
import ClientCollection from '../ContentCloud/Content/ClientCollection';
import { WithContentCloud } from '../ContentCloud/WithContentCloud';
import { ContentCloudPermission } from '../ContentCloud/shared-permissions';
import { SiteList } from './SiteList';
import { SiteDetail } from './SiteDetail';
import { ProjectLinkList } from './ProjectLinkList';
import { FullWidthTabsPageLayout } from '../../Layouts/FullWidthTabsPageLayout';

interface IExternalProps {
  tab: 'notifications' | 'overview' | 'locales' | 'environments' | 'clients' | 'links' | 'sites';
  id?: string;
}
interface IProps extends React.JSX.IntrinsicAttributes, IExternalProps, IProjectParamProp, IAppContextProp, INavigateProp, ILocationProp {}

interface IState extends IApiComponentState {
  project?: ClientProjectEntity;
  editProject?: ClientProjectEntity;
  syncCore?: ClientRemoteServiceEntity;
  anyActiveSite?: ClientSiteEntity;
  saving?: boolean;
  savingEnabledProducts?: boolean;
  editingEnabledProducts?: boolean;
  enabledProductsDraft?: Product[];
  confirmAction?: 'deactivate' | 'reactivate';
}

class ProjectDetailComponent extends ApiComponent<IProps, IState> {
  async load() {
    const project = await this.api.syndication.projects.item(this.props.project);

    this.props.appContext.setProject?.(project);

    let syncCore: ClientRemoteServiceEntity | undefined = undefined;
    let anyActiveSite: ClientSiteEntity | undefined = undefined;

    if (
      this.api.currentUser?.customerRoles?.includes(CustomerRole.Owner) ||
      this.api.currentUser?.customerRoles?.includes(CustomerRole.ManageSites)
    ) {
      syncCore = await project?.syncCore?.get();
      const sites = await this.api.billing.sites.search({ itemsPerPage: 1, page: 0 }, { projectId: project?.id });
      anyActiveSite = sites.items[0];
    }

    return {
      project,
      syncCore,
      anyActiveSite,
    };
  }

  render() {
    const { tab, id } = this.props;

    const {
      project,
      editProject,
      saving,
      savingEnabledProducts,
      editingEnabledProducts,
      enabledProductsDraft,
      confirmAction,
      syncCore,
      anyActiveSite,
    } = this.state;

    if (!project) {
      return this.renderRequest();
    }

    const renderBackendProperties = this.api.currentUser?.type === UserType.Internal;
    const canEditEnabledProducts = renderBackendProperties && this.api.currentUser?.internalRoles?.includes(InternalRole.Admin);
    const notificationEmbed = syncCore?.getAppType() === SiteApplicationType.SyncCore && anyActiveSite && (
      <>
        <ContentBox className="pb-3">
          <Embed embed="notifications" scope="configuration" syncCore={syncCore} site={anyActiveSite} />
        </ContentBox>
      </>
    );

    return (
      <>
        <FullWidthTabsPageLayout
          size={'large'}
          tabs={
            <Nav
              variant="tabs"
              id="project-tabs"
              className={'m-0 ps-2 pe-2 pt-0 pb-0'}
              activeKey={tab}
              onSelect={(activeTab) => this.props.navigate(`/projects/${project.id}/settings/${activeTab}`)}
            >
              <Nav.Item>
                <Nav.Link eventKey="overview">Overview</Nav.Link>
              </Nav.Item>
              {project.appType === SiteApplicationType.ContentCloud ? null : (
                <Nav.Item>
                  <Nav.Link eventKey="sites">Sites</Nav.Link>
                </Nav.Item>
              )}
              {syncCore?.getAppType() === SiteApplicationType.ContentCloud
                ? [
                    <Nav.Item key="environments">
                      <Nav.Link eventKey="environments">Environments</Nav.Link>
                    </Nav.Item>,
                    <Nav.Item key="locales">
                      <Nav.Link eventKey="locales">Locales</Nav.Link>
                    </Nav.Item>,
                    <Nav.Item key="clients">
                      <Nav.Link eventKey="clients">Authorization (clients)</Nav.Link>
                    </Nav.Item>,
                  ]
                : null}
              {notificationEmbed ? (
                <Nav.Item>
                  <Nav.Link eventKey="notifications">Notifications</Nav.Link>
                </Nav.Item>
              ) : null}
            </Nav>
          }
        >
          {tab === 'overview' && (
            <div className="pb-3 p-3">
              <LeftRightContainer
                className="mb-4"
                left={
                  <h2 className="mb-0 text-truncate mw-100">
                    <ProjectIcons project={project} />
                    <span className="ms-3" title={project.name}>
                      {project.name}
                    </span>
                  </h2>
                }
                right={
                  <>
                    {!project.status || project.status === ProjectStatus.Active ? (
                      <HasNone request={(api) => api.billing.sites.search(undefined, { projectId: project.id })}>
                        <Button variant="danger" onClick={() => this.setState({ confirmAction: 'deactivate' })}>
                          Deactivate
                        </Button>
                      </HasNone>
                    ) : (
                      <Button variant="primary" onClick={() => this.setState({ confirmAction: 'reactivate' })}>
                        Reactivate
                      </Button>
                    )}
                    {editProject ? null : (
                      <UserRoleGate allowed={[CustomerRole.Owner, CustomerRole.ManageSites]}>
                        {() => <EditButton onClick={() => this.setState({ editProject: new ClientProjectEntity(project) })} />}
                      </UserRoleGate>
                    )}
                  </>
                }
              />

              <Row>
                <Col xs={6} className={'p-5'}>
                  <Image style={{ marginLeft: '25%' }} src={ProjectDetailImage} width={'50%'} />
                </Col>
                <Col className={'pt-4'}>
                  {editProject ? (
                    <ProjectForm
                      fixedContract
                      key={editProject ? 'edit' : 'view'}
                      name={<></>}
                      onSave={async (update) => {
                        this.setState({
                          saving: true,
                        });
                        try {
                          const project = await this.api.syndication.projects.update(update as ClientProjectEntity);
                          this.setState({
                            project,
                            editProject: undefined,
                          });

                          this.props.appContext.refreshProjects?.();
                        } finally {
                          this.setState({
                            saving: false,
                          });
                        }
                      }}
                      mode={editProject ? 'edit-full' : 'view-full'}
                      entity={editProject || project}
                    >
                      {editProject && (
                        <Right>
                          <Button disabled={saving} variant="light" onClick={() => this.setState({ editProject: undefined })}>
                            Cancel
                          </Button>
                          <Button disabled={saving} type="submit" variant="primary">
                            Save
                          </Button>
                        </Right>
                      )}
                    </ProjectForm>
                  ) : (
                    <>
                      <h4>Details</h4>
                      {renderBackendProperties && (
                        <IconSummaryItem icon={faBuilding}>
                          <CustomerLink entityId={project.customer.getId()} />
                        </IconSummaryItem>
                      )}
                      <IconSummaryItem icon={faDollarSign}>
                        <ContractLink entityId={project.contract.getId()} />
                      </IconSummaryItem>
                      {!!project.syncCore?.getId() && (
                        <IconSummaryItem icon={faServer}>
                          <SyncCoreLink entityId={project.syncCore.getId()!} />
                        </IconSummaryItem>
                      )}
                      {syncCore?.getAppType() === SiteApplicationType.ContentCloud && (
                        <WithContentCloud permissions={[ContentCloudPermission.SPACE_READ]}>
                          {(data) => <IconSummaryItem icon={faIdCard}>{data?.space.id}</IconSummaryItem>}
                        </WithContentCloud>
                      )}
                      <IconSummaryItem icon={faFingerprint}>{project.uuid}</IconSummaryItem>

                      {renderBackendProperties && (
                        <div className="mt-3">
                          <LeftRightContainer
                            left={
                              <div>
                                <strong>Enabled products</strong>
                                <div className="mt-1">
                                  {project.enabledProducts?.length ? (
                                    project.enabledProducts.map((product, index) => (
                                      <span key={product}>
                                        <SyncCoreDataDefinitionsEnumValue enumName={'Product'} keyName={product} />
                                        {index < project.enabledProducts!.length - 1 ? ', ' : ''}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-muted">None</span>
                                  )}
                                </div>
                              </div>
                            }
                            right={
                              canEditEnabledProducts && !editingEnabledProducts ? (
                                <EditButton
                                  onClick={() =>
                                    this.setState({
                                      editingEnabledProducts: true,
                                      enabledProductsDraft: [...(project.enabledProducts || [])],
                                    })
                                  }
                                />
                              ) : (
                                <></>
                              )
                            }
                          />

                          {editingEnabledProducts && (
                            <div className="mt-2">
                              <div>
                                {(Object.keys(SyncCoreDataDefinitionsEnumTranslator.translateEnum('Product')) as Product[]).map(
                                  (product) => {
                                    const label = SyncCoreDataDefinitionsEnumTranslator.translateEnum('Product')[product];
                                    const selected = (enabledProductsDraft || []).includes(product);
                                    const disabled = !canEditEnabledProducts || !!savingEnabledProducts;

                                    return (
                                      <Form.Check
                                        key={product}
                                        type="checkbox"
                                        id={`enabled-products-${product}`}
                                        label={label}
                                        checked={selected}
                                        disabled={disabled}
                                        onChange={() => {
                                          const current = enabledProductsDraft || [];
                                          const next = selected ? current.filter((p) => p !== product) : [...current, product];
                                          this.setState({ enabledProductsDraft: next });
                                        }}
                                      />
                                    );
                                  }
                                )}
                              </div>
                              <Right>
                                <Button
                                  disabled={!!savingEnabledProducts}
                                  variant="light"
                                  onClick={() =>
                                    this.setState({
                                      editingEnabledProducts: false,
                                      enabledProductsDraft: undefined,
                                    })
                                  }
                                >
                                  Cancel
                                </Button>
                                <Button
                                  disabled={!!savingEnabledProducts || !canEditEnabledProducts || !(enabledProductsDraft || []).length}
                                  variant="primary"
                                  onClick={async () => {
                                    if (!canEditEnabledProducts) {
                                      return;
                                    }
                                    this.setState({ savingEnabledProducts: true });
                                    try {
                                      const updated = await this.api.syndication.projects.setEnabledProducts(
                                        project.id,
                                        enabledProductsDraft || []
                                      );
                                      this.setState({
                                        project: updated,
                                        editingEnabledProducts: false,
                                        enabledProductsDraft: undefined,
                                      });
                                      this.props.appContext.refreshProjects?.();
                                    } finally {
                                      this.setState({ savingEnabledProducts: false });
                                    }
                                  }}
                                >
                                  Save
                                </Button>
                              </Right>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </div>
          )}

          {tab === 'notifications' && notificationEmbed}

          {tab === 'links' && (
            <ProjectLinkList projectConnection={project.appType === SiteApplicationType.ContentCloud ? 'target' : 'source'} />
          )}

          {tab === 'sites' && project.appType !== SiteApplicationType.ContentCloud && (id ? <SiteDetail /> : <SiteList />)}

          {tab === 'environments' && syncCore?.getAppType() === SiteApplicationType.ContentCloud && (
            <ContentBox className="pb-3">
              <EnvironmentCollection />
            </ContentBox>
          )}

          {tab === 'locales' && syncCore?.getAppType() === SiteApplicationType.ContentCloud && (
            <ContentBox className="pb-3">
              <LocaleCollection />
            </ContentBox>
          )}

          {tab === 'clients' && syncCore?.getAppType() === SiteApplicationType.ContentCloud && (
            <ContentBox className="pb-3">
              <ClientCollection />
            </ContentBox>
          )}
        </FullWidthTabsPageLayout>

        <Modal show={!!confirmAction} scrollable onHide={() => this.setState({ confirmAction: undefined })}>
          <Modal.Header closeButton>
            <Modal.Title>{confirmAction === 'deactivate' ? 'Deactivate project' : 'Reactivate project'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {confirmAction === 'deactivate' ? (
              <>
                <strong>Are you sure you want to deactivate this project?</strong>
                <br />
                <br />
                You won't be able to add new sites to this project or reactivate existing sites, but you can reactivate the project later.
              </>
            ) : (
              <>
                <strong>Are you sure you want to reactivate this project?</strong>
                <br />
                <br />
                You will be able to add new sites to this project again and reactivate sites in this project.
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Right>
              <Button
                disabled={saving}
                variant={confirmAction === 'deactivate' ? 'danger' : 'primary'}
                onClick={async () => {
                  this.setState({ saving: true });
                  try {
                    project.status = confirmAction === 'deactivate' ? ProjectStatus.Inactive : ProjectStatus.Active;
                    const updatedProject = await this.api.syndication.projects.update(project);
                    this.setState({ project: updatedProject, confirmAction: undefined });
                  } finally {
                    this.setState({ saving: false });
                  }
                }}
              >
                Continue
              </Button>
            </Right>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}

export const ProjectDetail = withProjectParam<IExternalProps>(
  withLocationAndNavigate(propertyChangeReload<any>(withAppContext<any>(ProjectDetailComponent), (props) => props.project))
);
